import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useDebounce } from 'usehooks-ts';
import { useSearchParams } from 'react-router-dom';
import isArray from 'lodash/isArray';
import * as Form from '@radix-ui/react-form';
import useScreenType from '@/hooks/util/useScreenType';
import type { PreRegistrationListFormType } from '../../types';
import {
  OPTIONS_STATUS_PRE_REGISTRATION,
  PRE_REGISTRATION_LIST_SCHEMA,
} from '../../constants';
import styles from './form-search.module.scss';
import type { FormSearchProps } from './types';
import { setFilterCityOnApprovalsList } from '@/_store/modules/filters/slice';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import { useCities } from '@/_hooks/use-cities';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import { CalendarInput } from '@/_components/calendar-input/calendar-input';
import { VEHICLE_TYPES_OPTIONS } from '@/_features/motoboys/deliveryman-form/constants';

function FormSearch({ onRequest }: FormSearchProps) {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useScreenType('mobile');

  const preRegistrationFilters = (() => {
    const filters = searchParams.get('status')?.split(',');
    if (filters && filters[0] === '') {
      return [];
    }
    return filters ?? [];
  })();

  const filteredCityId = useAppSelector(
    state => state.filters.cityIdOnApprovalsList,
  );

  useEffect(() => {
    if (filteredCityId) {
      setSearchParams(prev => {
        prev.set('cityId', filteredCityId);
        return prev;
      });
    }
  }, [filteredCityId]);

  const startDate = searchParams.get('startDate');
  const vehicleType = searchParams.get('vehicleType');

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<PreRegistrationListFormType>({
    resolver: yupResolver(PRE_REGISTRATION_LIST_SCHEMA),
    values: {
      deliverymanName: searchParams.get('deliverymanName') || '',
      cityId: filteredCityId || searchParams.get('cityId') || '',
      preRegistrationFilters,
      sendPreRegistrationAt: startDate ? new Date(startDate) : undefined,
      vehicleType: vehicleType || '',
    },
  });

  const { CITIES_OPTIONS_WITH_ALL, loadingCities } = useCities();

  const watchDeliverymaName = useDebounce(watch('deliverymanName'));

  useEffect(() => {
    onRequest(getValues());
  }, [watchDeliverymaName]);

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name !== 'deliverymanName') {
        handleSubmit(submitForm)();
      }
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  const submitForm = (data: PreRegistrationListFormType) => {
    setTimeout(() => onRequest(data), 500);
  };

  return (
    <Form.Root onSubmit={handleSubmit(submitForm)}>
      <div className={styles.formContainer}>
        <div>
          <Controller
            control={control}
            name="cityId"
            render={({ field: { onChange, value } }) => {
              return (
                <DropdownInput
                  data-testid="select-city"
                  clearable
                  label="Município"
                  options={CITIES_OPTIONS_WITH_ALL}
                  value={value}
                  loading={loadingCities}
                  search
                  placeholder="Município"
                  onChange={value => {
                    dispatch(setFilterCityOnApprovalsList(value as string));
                    setSearchParams(searchParams => {
                      const newSearchParams = new URLSearchParams(searchParams);
                      newSearchParams.set('cityId', value?.toString() || '');
                      return newSearchParams;
                    });
                    onChange(value || '');
                  }}
                  errorMessage={errors.cityId?.message}
                />
              );
            }}
          />
        </div>
        <div>
          <Controller
            name="deliverymanName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="deliverymanName"
                value={value}
                data-testid="input-deliveryman-name"
                width="100%"
                onChange={data => {
                  setSearchParams(searchParams => {
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('deliverymanName', data.target.value);
                    return newSearchParams;
                  });
                  onChange(data);
                }}
                label="Nome"
                placeholder="Nome do entregador"
                errorMessage={errors.deliverymanName?.message}
              />
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="preRegistrationFilters"
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                label="Filtros"
                data-testid="select-status"
                options={OPTIONS_STATUS_PRE_REGISTRATION}
                value={value as string[]}
                search
                multiple
                placeholder="Selecionar filtro"
                onChange={value => {
                  setSearchParams(searchParams => {
                    const newSearchParams = new URLSearchParams(searchParams);
                    if (isArray(value)) {
                      newSearchParams.set('status', value.join(','));
                      return newSearchParams;
                    }
                    newSearchParams.set('status', value?.toString() || '');
                    return newSearchParams;
                  });
                  onChange(value);
                }}
              />
            )}
          />
        </div>
        <div>
          <Controller
            name="sendPreRegistrationAt"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div>
                <CalendarInput
                  name="sendPreRegistrationAt"
                  value={value}
                  clearable
                  data-testid="date-pre-registration"
                  label="Data da finalização do cadastro"
                  selectsStart
                  startDate={value}
                  onChange={(date: Date | null) => {
                    if (!date) {
                      setSearchParams(data => {
                        const newSearchParams = new URLSearchParams(data);
                        newSearchParams.delete('startDate');
                        return newSearchParams;
                      });
                      return onChange(undefined);
                    }
                    setSearchParams(data => {
                      const newSearchParams = new URLSearchParams(data);
                      newSearchParams.set('startDate', date.toISOString());
                      return newSearchParams;
                    });
                    onChange(date);
                  }}
                  withPortal={isMobile}
                  maxDate={new Date()}
                  todayButton="Hoje"
                  timeInputLabel="Horário:"
                  showTimeInput
                  popperPlacement="top"
                />
                {errors.sendPreRegistrationAt ? (
                  <span className="text-error">
                    {errors.sendPreRegistrationAt.message}
                  </span>
                ) : null}
              </div>
            )}
          />
        </div>
        <div>
          <Controller
            name="vehicleType"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                name="vehicleType"
                label="Tipo de veículo"
                options={VEHICLE_TYPES_OPTIONS}
                onChange={value => {
                  setSearchParams(searchParams => {
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.set('vehicleType', value?.toString() || '');
                    return newSearchParams;
                  });
                  onChange(value);
                }}
                value={value}
                placeholder="Tipo do veículo"
                clearable
              />
            )}
          />
        </div>
      </div>
    </Form.Root>
  );
}

export { FormSearch };
